import { EMICalculator } from "@/components/EMICalculator";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { PRODUCTS } from "@/lib/products";
import { inrShort } from "@/lib/format";

export default function EMIPage() {
  useSEO({
    title: "EMI Calculator — Personal, Home, Business & Property Loans | Loan Salaah",
    description: "Instant EMI calculator for six loan products. Adjust amount, rate and tenure; see your EMI, total interest and principal-vs-interest split live.",
  });

  return (
    <div data-testid="emi-page" className="pb-24 pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">The centrepiece</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl lg:text-6xl">
            Every loan begins with one honest number.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-slate">
            Your EMI. Slide the amount, rate and tenure — the maths updates instantly, entirely on your device.
            When the number feels right, apply with one tap.
          </p>
        </Reveal>

        <Reveal delay={0.12} className="mt-12">
          <EMICalculator />
        </Reveal>

        <Reveal delay={0.1} className="mt-16">
          <h2 className="font-serif text-2xl font-semibold text-concordia sm:text-3xl">Indicative starting rates</h2>
          <div className="mt-6 overflow-x-auto rounded-3xl border border-sand bg-white" data-testid="emi-rate-table">
            <table className="w-full min-w-[36rem] text-left text-sm">
              <thead>
                <tr className="border-b border-sand font-mono text-[0.62rem] uppercase tracking-[0.22em] text-slate">
                  <th className="px-6 py-4 font-medium">Product</th>
                  <th className="px-6 py-4 font-medium">Indicative rate*</th>
                  <th className="px-6 py-4 font-medium">Up to</th>
                  <th className="px-6 py-4 font-medium">Max tenure</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sand/70">
                {PRODUCTS.map((p) => (
                  <tr key={p.slug} className="transition-colors hover:bg-cream">
                    <td className="px-6 py-4 font-semibold text-concordia">{p.name}</td>
                    <td className="px-6 py-4">{p.rateMin}% – {p.rateMax}% p.a.</td>
                    <td className="px-6 py-4">{inrShort(p.amountMax)}</td>
                    <td className="px-6 py-4">{Math.round(p.tenureMax / 12)} years</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 max-w-2xl text-xs leading-relaxed text-slate/80">
            *Indicative placeholders shown for planning. Your sanctioned rate depends on the lender's assessment of your
            credit profile, income and the specific product. The calculator's output is an estimate, not an offer.
          </p>
          <p className="mt-6 text-sm text-slate">
            Not sure how much you can borrow in the first place?{" "}
            <a href="/eligibility-calculator" data-testid="emi-eligibility-crosslink" className="font-semibold text-concordia underline decoration-brass/50 underline-offset-4 transition-colors hover:text-brass">
              Try the eligibility calculator
            </a>
            .
          </p>
        </Reveal>
      </div>
    </div>
  );
}
